import { defineStore } from 'pinia';
import { AnalyticsBrowser, EventProperties } from '@segment/analytics-next';
import mixpanel from 'mixpanel-browser';
import { useUserStore } from 'src/core/stores/user';
import { AnalyticsPageLoad } from 'src/core/types/analytics/global';
import Cookies from 'js-cookie';

type AnalyticsStore = {
  analytics: AnalyticsBrowser | null;
  errorWithAnalytics: boolean;
  userEmail: string;
  mixpanelMiddlewareAdded: boolean;
};

/**
 * Represents the analytics store.
 */
export const useAnalyticsStore = defineStore('analytics', {
  state: () =>
    ({
      analytics: new AnalyticsBrowser(),
      errorWithAnalytics: false,
      mixpanelMiddlewareAdded: false,
      userEmail: '',
    } as AnalyticsStore),

  getters: {},

  actions: {
    async initialize() {
      if (import.meta.env.VITE_APP_SEGMENT_KEY) {
        this.analytics = new AnalyticsBrowser();
        await this.analytics
          .load({
            writeKey: import.meta.env.VITE_APP_SEGMENT_KEY,
          })
          .catch(() => (this.errorWithAnalytics = true));
      } else {
        this.errorWithAnalytics = true;
      }
    },

    trackingDisabled(email?: string): boolean {
      const emailToCheck = email ? email : this.userEmail;

      // Disable tracking if the `disable-segment` cookie is set.
      if (Cookies.get('disable-segment')) {
        return true;
      }

      // Disable tracking for impersonated users.
      if (Cookies.get('impersonateUser')) {
        return true;
      }

      // Disable tracking for meridia.land emails in production, unless the `enable-meridia-prod` cookie is set.
      if (isAMeridiaUser(emailToCheck) && import.meta.env.VITE_ENV === 'prod') {
        return !Cookies.get('enable-meridia-prod');
      }

      // If none of the above conditions are met, tracking is enabled.
      return false;
    },
    initializeMixpanel() {
      const userStore = useUserStore();
      if (
        import.meta.env.VITE_APP_MIXPANEL_KEY &&
        import.meta.env.VITE_ENV === 'prod' &&
        userStore.user &&
        !this.trackingDisabled(userStore.user.email)
      ) {
        mixpanel.init(import.meta.env.VITE_APP_MIXPANEL_KEY, {
          record_sessions_percent: 50, //records 50% of all sessions
          record_mask_text_selector: '', // Removes the *** of the texts, not the inputs
          record_heatmap_data: true, // Enables heatmap click data collection
          debug: import.meta.env.VITE_ENV !== 'prod',
        });
      }
    },
    /**
     * Identifies the user for analytics purposes.
     */
    identifyUser() {
      if (!this.errorWithAnalytics) {
        const userStore = useUserStore();
        if (userStore.user) {
          this.userEmail = userStore.user.email;
          if (!this.trackingDisabled(userStore.user.email)) {
            const userProperties = {
              name: userStore.getFullUserName,
              email: userStore.user?.email,
              customer_id: userStore.user?.customer?.id,
              user_role: userStore.getRole(userStore?.user),
            };
            // Update Segment
            this.analytics?.identify(
              String(userStore.user?.id),
              userProperties
            );
            if (import.meta.env.VITE_ENV === 'prod') {
              // Update Mixpanel
              this.recordMixpanel();
              if (mixpanel && this.mixpanelMiddlewareAdded) {
                // connect segment with mixpanel for replay
                mixpanel.identify(String(userStore.user?.id));
                mixpanel.people.set(userProperties);
              }
            }
          }
        }
      }
    },
    recordMixpanel() {
      const userStore = useUserStore();
      if (
        !userStore.user ||
        (userStore.user && this.trackingDisabled(userStore.user?.email))
      ) {
        return;
      }
      if (this.mixpanelMiddlewareAdded) {
        return;
      }
      this.initializeMixpanel();
      this.analytics?.addSourceMiddleware(({ payload, next }) => {
        try {
          if (payload.obj.type === 'track' || payload.obj.type === 'page') {
            if (mixpanel && this.mixpanelMiddlewareAdded) {
              const segmentDeviceId = payload.obj.anonymousId;
              // Simplified ID Merge
              mixpanel.register({
                $device_id: segmentDeviceId,
                distinct_id: '$device:' + segmentDeviceId,
              });
              const sessionReplayProperties =
                mixpanel.get_session_recording_properties();
              payload.obj.properties = {
                ...payload.obj.properties,
                ...sessionReplayProperties,
              };
            }
          }
          if (payload.obj.type === 'identify' && mixpanel) {
            const userId = payload.obj.userId;
            if (userId) {
              mixpanel.identify(String(userId));
            }
          }
        } catch (error) {
          console.error('Error in Mixpanel middleware:', error);
        } finally {
          next(payload);
        }
      });
      this.mixpanelMiddlewareAdded = true;
    },
    logoutMixpanel() {
      const userStore = useUserStore();
      if (this.trackingDisabled(userStore.user?.email)) {
        return;
      }
      if (
        mixpanel &&
        this.mixpanelMiddlewareAdded &&
        import.meta.env.VITE_ENV === 'prod'
      ) {
        mixpanel.stop_session_recording();
        mixpanel.reset();
      }
    },
    /**
     * Tracks an event with the specified properties.
     * @param name
     * @param properties - The properties associated with the event.
     */
    track<T extends EventProperties>(name: string, properties?: T) {
      if (!this.trackingDisabled()) {
        if (!this.errorWithAnalytics) {
          const extraProps = {
            ...properties,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
          };

          this.analytics?.track(name, extraProps);
        }
      }
    },

    /**
     * Sends a page load event to the analytics service.
     *
     * @param name - The name of the page.
     * @param properties - Optional properties associated with the page load event.
     */
    page(name: string, properties?: AnalyticsPageLoad) {
      if (!this.trackingDisabled()) {
        if (!this.errorWithAnalytics) {
          const extraProps = {
            ...properties,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
          };
          this.analytics?.page(name, extraProps);
        }
      }
    },

    /**
     * waits for it to be ready.
     * @returns A promise that resolves when the analytics module is ready.
     */
    async ready() {
      if (!this.errorWithAnalytics) {
        await this.analytics?.ready();
      }
    },

    /**
     * Enables or disables debug mode for analytics.
     * @param state - A boolean value indicating whether debug mode should be enabled or disabled.
     */
    switchDebug(state: boolean) {
      this.analytics?.debug(state);
    },
  },
});

function isAMeridiaUser(email: string): boolean {
  return email.includes('meridia.land');
}
